import React from 'react';
import math from 'polished/lib/math/math';

import { useTheme } from '@veneer/theme';
import { SerializedStyles, css } from '@emotion/react';
import { focusRingStyle } from '../utils/focus_ring';

type StylesType = {
  root: SerializedStyles;
  select: {
    base: SerializedStyles;
    open: {
      base: SerializedStyles;
      hc: SerializedStyles;
    };
    // When we drop React 16, use css selectors &:has(+ input:disabled) and merge with container.input.base
    error: SerializedStyles;
    disabled: SerializedStyles;
  };
  placeholder: {
    base: SerializedStyles;
    disabled: SerializedStyles;
    ellipsis: SerializedStyles;
    withLabel: SerializedStyles;
  };
  clearButton: {
    base: SerializedStyles;
    disabled: SerializedStyles;
  };
  arrowIcon: {
    base: SerializedStyles;
    open: SerializedStyles;
  };
  content: {
    base: SerializedStyles;
    hc: SerializedStyles;
    hcFocusedOrOpen: SerializedStyles;
    pill: SerializedStyles;
  };
  searchDiv: {
    base: SerializedStyles;
  };
  searchInput: SerializedStyles;
  searchIcon: {
    base: SerializedStyles;
    pill: SerializedStyles;
  };
  noResults: SerializedStyles;
  showingResultsDivider: SerializedStyles;
  buttonTitle: SerializedStyles;
  inputLabel: SerializedStyles;
  titleIcon: SerializedStyles;
  titleLabel: SerializedStyles;
  titleLabelEllipsis: SerializedStyles;
};

const useStyles = (): StylesType => {
  const theme = useTheme();

  return React.useMemo(() => {
    return {
      root: css({
        boxSizing: 'border-box',
        position: 'relative',
        display: 'flex',

        transition:
          'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
      }),
      select: {
        // maybe this base style can be replaced by container.input.base style.
        base: css({
          cursor: 'pointer',
          display: 'inline-flex',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          width: '100%',

          backgroundColor: theme.color.background.container.default,
          border: `1px solid ${theme.color.neutral.base.default}`,
          borderRadius: theme.borderRadius,
          color: theme.color.foreground.default,

          '&:hover': {
            borderColor: theme.color.neutral.base.hover,
            backgroundColor: theme.color.background.container.hover,
          },

          '&:focus': {
            ...focusRingStyle({
              theme,
              outlineOffset: -2,
              shadowSize: 0,
            }),
            outlineColor: theme.color.primary.base.default,
            '&:hover': {
              backgroundColor: theme.color.background.container.default,
              border: `1px solid ${theme.color.neutral.base.default}`,
            },
          },
        }),
        error: css({
          borderColor: theme.color.negative.base.default,
          '&:hover': { borderColor: theme.color.negative.base.hover },
        }),
        disabled: css({
          backgroundColor: theme.color.background.disabled,
          borderColor: theme.color.foreground.disabled,
          outline: 'none',
          color: theme.color.foreground.disabled,
          cursor: 'unset',
          pointerEvents: 'none',
        }),
        open: {
          base: css({
            ...focusRingStyle({
              theme,
              outlineOffset: -2,
              shadowSize: 0,
            }),
            outlineColor: theme.color.primary.base.default,
          }),
          hc: css({
            outlineColor: theme.color.primary.base.active,
          }),
        },
      },
      placeholder: {
        base: css({
          color: theme.color.foreground.light,
          display: 'flex',
          margin: '2px 0',
          overflow: 'hidden',
          transition: 'opacity 100ms cubic-bezier(0, 0, 0.2, 1)',
        }),
        disabled: css({
          color: theme.color.foreground.disabled,
        }),
        withLabel: css({
          visibility: 'hidden',
        }),
        ellipsis: css({
          display: 'inline-block',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }),
      },

      clearButton: {
        base: css({
          '> svg': {
            fill: theme.color.neutral.base.default,
          },
        }),

        disabled: css({
          '> svg': {
            fill: theme.color.foreground.disabled,
          },
        }),
      },
      arrowIcon: {
        base: css({
          transition: 'transform 150ms cubic-bezier(0, 0, 0.2, 1)',
          willChange: 'transform',
        }),
        open: css({
          transform: 'rotate(180deg)',
        }),
      },
      // Popover
      content: {
        base: css({
          display: 'flex',
          overflow: 'hidden',
          flexDirection: 'column',
          backgroundColor: theme.color.background.container.default,
          borderRadius: theme.borderRadius4,
          boxShadow: `${theme.elevation.level16.boxShadow} ${theme.color.effects.dropShadow}`,
        }),
        hc: css({
          borderColor: theme.color.primary.base.active,
          borderStyle: 'solid',
          borderWidth: '2px',
        }),
        hcFocusedOrOpen: css({
          borderWidth: '2px',
        }),
        pill: css({
          borderRadius: theme.borderRadius9,
        }),
      },
      searchDiv: {
        base: css({
          position: 'relative',
          borderBottom: `1px solid ${theme.color.stroke.decorative.default}`,
        }),
      },
      searchInput: css({
        ...theme.typography.labelRegular,
        background: 'transparent',
        border: 0,
        boxSizing: 'border-box',
        color: theme.color.foreground.default,
        margin: 0,
        padding: theme.layout.size3,
        paddingRight: math(`4 * ${theme.layout.size3}`),
        paddingLeft: theme.layout.size4,
        width: '100%',

        '::placeholder': {
          color: theme.color.foreground.light,
          opacity: 1 /* Firefox */,
        },

        /* Safari compatibility */
        opacity: 1,
        WebkitTextFillColor: 'currentColor',

        '&:focus': {
          outline: 0,
        },
      }),
      searchIcon: {
        base: css({
          fill: theme.color.foreground.light,
          pointerEvents: 'none',
          position: 'absolute',
          right: theme.layout.size3,
          top: '50%',
          transform: 'translateY(-50%)',
        }),
        pill: css({
          right: theme.layout.size4,
        }),
      },
      noResults: css({
        ...theme.typography.labelRegular,
        boxSizing: 'border-box',
        color: theme.color.foreground.medium,
        display: 'block',
        padding: [`calc(${theme.layout.size4} + 2px)`, theme.layout.size2].join(
          ' '
        ),
        textAlign: 'center',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
      }),
      showingResultsDivider: css({
        border: '0 !important',
        paddingBottom: theme.layout.size2,
      }),
      buttonTitle: css({
        margin: '2px 0',
        display: 'block',
      }),
      inputLabel: css({
        cursor: 'pointer',
      }),
      titleIcon: css({
        alignItems: 'center',
        display: 'flex',
        svg: { marginRight: theme.orbitalTwo },
      }),
      titleLabel: css({
        display: 'inline-block',
        margin: '2px 0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
      titleLabelEllipsis: css({
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        margin: '2px 0',
      }),
    };
  }, [theme]);
};

export default useStyles;
